import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { orpc } from "@/frontend/orpc-client";
import { formatDateTime } from "@/frontend/utils/shared";
import { KIND_ICON } from "@/frontend/types/dashboard/patient-activity";

/**
 * Recent activity for one patient, newest first. Includes what the patient did
 * through the public portal, along with what was done in the workspace. Renders nothing
 * when there is no activity.
 */
export function RecentActivityCard({ patientId }: { patientId: string }) {
  const { data, isLoading } = useQuery(
    orpc.admin.getPatientActivity.queryOptions({ input: { patientMembershipId: patientId } }),
  );

  const events = data?.events ?? [];

  // Nothing to show and nothing still coming: render no card at all.
  if (!isLoading && events.length === 0) return null;

  return (
    <section className="flex max-h-full min-h-0 flex-col overflow-hidden rounded-lg border border-border/60 bg-card">
      <div className="flex h-10 shrink-0 items-center justify-between gap-2 border-b border-border/60 px-4">
        <h2 className="text-meta font-semibold uppercase tracking-wide text-foreground">
          Recent activity
        </h2>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="min-h-0 flex-1 divide-y divide-border/60 overflow-y-auto">
          {events.map((event, index) => {
            const { Icon, className } = KIND_ICON[event.kind];
            return (
              <div key={`${event.kind}-${event.at.toISOString()}-${index}`} className="flex items-start gap-2.5 px-4 py-3">
                <Icon className={`mt-0.5 size-4 shrink-0 ${className}`} />
                <div className="min-w-0 flex-1">
                  <div className="line-clamp-2 break-words text-body font-medium" data-patient>
                    {event.title}
                  </div>
                  {event.detail && (
                    <div className="break-words text-caption text-muted-foreground">{event.detail}</div>
                  )}
                </div>
                <span className="shrink-0 text-meta text-muted-foreground">
                  {formatDateTime(event.at)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
